import { supabase } from './supabase';

/**
 * Enhanced Login Session Management Service
 * Handles login tracking, session management, and security monitoring
 */

// Login session tracking
export const loginSessionService = {
  // Track new login attempt
  async trackLogin(userAgent = navigator.userAgent) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Get IP address and location (mock implementation)
      const sessionData = await this.getSessionInfo(userAgent);
      
      // Store login session in database
      const { data, error } = await supabase
        .from('login_sessions')
        .insert({
          user_id: user.id,
          session_id: this.generateSessionId(),
          device_info: sessionData.device,
          browser_info: sessionData.browser,
          location: sessionData.location,
          ip_address: sessionData.ip,
          user_agent: userAgent,
          login_time: new Date().toISOString(),
          is_current: true
        })
        .select()
        .single();

      if (error) throw error;

      // Mark previous sessions as not current
      await supabase
        .from('login_sessions')
        .update({ is_current: false })
        .neq('id', data.id)
        .eq('user_id', user.id);

      // Login notifications are permanently disabled (per user feedback)
      // Removed: this.checkAndSendLoginNotification(user.id, sessionData);

      return data;
    } catch (error) {
      console.error('Error tracking login:', error);
      return null;
    }
  },

  // Get current session information
  async getSessionInfo(userAgent) {
    try {
      // Parse user agent for device and browser info
      const deviceInfo = this.parseUserAgent(userAgent);
      
      // Get approximate location (in production, use proper geolocation service)
      const locationInfo = await this.getLocationInfo();

      return {
        device: deviceInfo.device,
        browser: deviceInfo.browser,
        location: locationInfo.location,
        ip: locationInfo.ip,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Error getting session info:', error);
      return {
        device: 'Unknown Device',
        browser: 'Unknown Browser',
        location: 'Unknown Location',
        ip: '0.0.0.0',
        timestamp: new Date()
      };
    }
  },

  // Parse user agent string
  parseUserAgent(userAgent) {
    const device = this.getDeviceType(userAgent);
    const browser = this.getBrowserInfo(userAgent);
    
    return { device, browser };
  },

  // Get device type from user agent
  getDeviceType(userAgent) {
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      if (/iPhone/.test(userAgent)) return 'iPhone';
      if (/iPad/.test(userAgent)) return 'iPad';
      if (/Android/.test(userAgent)) return 'Android Phone';
      return 'Mobile Device';
    }
    
    if (/Macintosh/.test(userAgent)) return 'MacBook';
    if (/Windows/.test(userAgent)) return 'Windows PC';
    if (/Linux/.test(userAgent)) return 'Linux PC';
    
    return 'Desktop Computer';
  },

  // Get browser info from user agent
  getBrowserInfo(userAgent) {
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) return 'Chrome';
    if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return 'Safari';
    if (/Firefox/.test(userAgent)) return 'Firefox';
    if (/Edg/.test(userAgent)) return 'Edge';
    if (/Opera/.test(userAgent)) return 'Opera';
    
    return 'Unknown Browser';
  },

  // Get location information (mock implementation)
  async getLocationInfo() {
    // In production, integrate with a geolocation service like ipapi.co or similar
    try {
      // Mock implementation - replace with actual geolocation service
      const mockLocations = [
        { location: 'New York, USA', ip: '192.168.1.1' },
        { location: 'Los Angeles, USA', ip: '192.168.1.2' },
        { location: 'Chicago, USA', ip: '192.168.1.3' },
        { location: 'Houston, USA', ip: '192.168.1.4' },
        { location: 'Miami, USA', ip: '192.168.1.5' }
      ];
      
      return mockLocations[Math.floor(Math.random() * mockLocations.length)];
    } catch (error) {
      return { location: 'Unknown Location', ip: '0.0.0.0' };
    }
  },

  // Generate unique session ID
  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  },

  // DEPRECATED: Login notifications permanently disabled per user feedback
  // This function is kept for backwards compatibility but does nothing
  async checkAndSendLoginNotification(userId, sessionData) {
    // Login notifications have been disabled
    // Previously sent "New Login Detected" notifications, now removed per user feedback
    return;
  },

  // Clean up existing login notification entries from the database
  async cleanupLoginNotifications(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .or('title.ilike.%login%,message.ilike.%login%')
        .in('type', ['security', 'system', 'login']);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error cleaning up login notifications:', error);
      return { success: false, error: error.message };
    }
  },

  // Fetch login history for user
  async getLoginHistory(userId, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('login_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('login_time', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching login history:', error);
      return [];
    }
  },

  // Sign out from all devices
  async signOutAllDevices() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'No user found' };

      // Mark all sessions as inactive
      await supabase
        .from('login_sessions')
        .update({ is_current: false, signed_out_at: new Date().toISOString() })
        .eq('user_id', user.id);

      // Sign out from Supabase (this will invalidate all tokens)
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      
      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error signing out all devices:', error);
      return { success: false, error: error.message };
    }
  },

  // Check for suspicious activity
  async checkSuspiciousActivity(userId) {
    try {
      const recentLogins = await this.getLoginHistory(userId, 20);
      const suspiciousFactors = [];

      // Check for multiple locations in short time
      const locationChanges = this.detectLocationChanges(recentLogins);
      if (locationChanges.length > 3) {
        suspiciousFactors.push('Multiple geographic locations');
      }

      // Check for unusual devices
      const deviceVariety = this.detectUnusualDevices(recentLogins);
      if (deviceVariety > 4) {
        suspiciousFactors.push('Multiple new devices');
      }

      // Check for rapid login attempts
      const rapidAttempts = this.detectRapidLogins(recentLogins);
      if (rapidAttempts) {
        suspiciousFactors.push('Rapid login attempts');
      }

      return {
        isSuspicious: suspiciousFactors.length > 0,
        factors: suspiciousFactors,
        severity: this.calculateSeverity(suspiciousFactors)
      };
    } catch (error) {
      console.error('Error checking suspicious activity:', error);
      return { isSuspicious: false, factors: [], severity: 'low' };
    }
  },

  // Helper methods for suspicious activity detection
  detectLocationChanges(logins) {
    const locations = [...new Set(logins.map(login => login.location))];
    return locations.slice(0, -1); // Exclude current location
  },

  detectUnusualDevices(logins) {
    const devices = [...new Set(logins.map(login => login.device_info))];
    return devices.length;
  },

  detectRapidLogins(logins) {
    if (logins.length < 2) return false;
    
    const timeThreshold = 5 * 60 * 1000; // 5 minutes
    for (let i = 1; i < Math.min(logins.length, 5); i++) {
      const timeDiff = new Date(logins[i-1].login_time) - new Date(logins[i].login_time);
      if (timeDiff < timeThreshold) {
        return true;
      }
    }
    return false;
  },

  calculateSeverity(factors) {
    if (factors.length >= 3) return 'high';
    if (factors.length >= 2) return 'medium';
    if (factors.length >= 1) return 'low';
    return 'none';
  },

  // Session timeout management
  setupSessionTimeout(timeoutMinutes) {
    if (timeoutMinutes === 0) return; // No timeout

    const timeoutMs = timeoutMinutes * 60 * 1000;
    
    // Clear existing timeout and remove old listeners
    if (window.sessionTimeoutId) {
      clearTimeout(window.sessionTimeoutId);
    }
    this.clearSessionTimeout();

    // Set new timeout
    window.sessionTimeoutId = setTimeout(async () => {
      try {
        await supabase.auth.signOut();
        window.location.href = '/login?reason=session_timeout';
      } catch (error) {
        console.error('Error during session timeout:', error);
      }
    }, timeoutMs);

    // Reset timeout on user activity
    const resetTimeout = () => {
      if (window.sessionTimeoutId) {
        clearTimeout(window.sessionTimeoutId);
        
        // Set new timeout without re-adding listeners
        window.sessionTimeoutId = setTimeout(async () => {
          try {
            await supabase.auth.signOut();
            window.location.href = '/login?reason=session_timeout';
          } catch (error) {
            console.error('Error during session timeout:', error);
          }
        }, timeoutMs);
      }
    };

    // Store resetTimeout function reference for cleanup
    window.sessionResetTimeout = resetTimeout;

    // Add activity listeners
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, resetTimeout, true);
    });
  },

  // Clear session timeout and remove all event listeners
  clearSessionTimeout() {
    if (window.sessionTimeoutId) {
      clearTimeout(window.sessionTimeoutId);
      delete window.sessionTimeoutId;
    }

    // Remove all activity listeners
    if (window.sessionResetTimeout) {
      ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
        document.removeEventListener(event, window.sessionResetTimeout, true);
      });
      delete window.sessionResetTimeout;
    }
  }
};

export default loginSessionService;